import type { SenderDomainRecord } from "../../../data/api";
import type { Pill } from "../../../model/invoice-view";
import { t, ta } from "../../../lib/sprache";

/**
 * Eigene Absenderdomain (0102): Stand mit Erklärung, DNS-Einträge zum Kopieren. Die Serverfunktion sender-domain legt die Domain
 * beim Versanddienst an und prüft sie; der Zeitplan prüft alle 15 Minuten. Reine Anzeige-Logik ohne Abfragen.
 */

export type SenderDomainStatus = "none" | "requested" | "pending" | "verified" | "failed";

const SYMBOL = { neutral: "○", info: "◇", warning: "◷", success: "✓", error: "!" } as const;
const pill = (label: string, tone: keyof typeof SYMBOL): Pill => ({ label, tone, symbol: SYMBOL[tone] });

/** Stand als Pille und ein Satz, was er bedeutet und was zu tun ist. */
export function senderDomainView(status: SenderDomainStatus, ready: boolean, address: string | null): { pill: Pill; text: string } {
  switch (status) {
    case "requested":
      return {
        pill: pill(t("Wird beim Versanddienst angelegt", "Being created at the email service"), "info"),
        text: t("Die Domain ist gespeichert. In wenigen Minuten erscheinen hier die DNS-Einträge, die ihr bei eurem Domain-Anbieter eintragt.",
          "The domain is saved. In a few minutes the DNS records to add at your domain provider appear here."),
      };
    case "pending":
      return {
        pill: pill(t("DNS-Prüfung läuft", "DNS check in progress"), "warning"),
        text: t("Tragt die Einträge unten bei eurem Domain-Anbieter ein. Die Prüfung läuft alle 15 Minuten von selbst; nach dem Eintragen dauert es meist wenige Minuten, manchmal bis zu 72 Stunden. Bis dahin gehen Mails über die Absenderadresse der Plattform.",
          "Add the records below at your domain provider. The check runs by itself every 15 minutes; after adding them it usually takes a few minutes, sometimes up to 72 hours. Until then emails go out via the platform's sender address."),
      };
    case "verified":
      return ready
        ? {
          pill: pill(t(`Aktiv: ${address ?? ""}`, `Active: ${address ?? ""}`).trim(), "success"),
          text: ta("Mails der Agentur gehen von dieser Adresse raus. Die Einträge bei eurem Domain-Anbieter bitte nicht löschen.",
            "Your agency's emails go out from this address. Please do not delete the records at your domain provider.", "Deine Mails gehen von dieser Adresse raus. Die Einträge bei deinem Domain-Anbieter bitte nicht löschen.", "Your emails go out from this address. Please do not delete the records at your domain provider."),
        }
        : {
          pill: pill(t(`Bestätigt: ${address ?? ""}`, `Verified: ${address ?? ""}`).trim(), "success"),
          text: t("Die Domain ist bestätigt. Aktiv wird sie, sobald der Plattform-Betrieb den Versand über eigene Domains freischaltet; bis dahin gehen Mails über die Absenderadresse der Plattform.",
            "The domain is verified. It becomes active as soon as platform operations enable sending via custom domains; until then emails go out via the platform's sender address."),
        };
    case "failed":
      return {
        pill: pill(t("Prüfung gescheitert", "Verification failed"), "error"),
        text: t("Die Einträge ließen sich nicht bestätigen. Prüft sie bei eurem Domain-Anbieter (Name, Typ und Wert genau wie unten) und wählt dann „Jetzt prüfen“.",
          "The records could not be verified. Check them at your domain provider (name, type and value exactly as below) and then choose “Check now”."),
      };
    default:
      return {
        pill: pill(t("Keine eigene Domain", "No custom domain"), "neutral"),
        text: ta("Mails gehen über die Absenderadresse der Plattform, mit dem Namen der Agentur als Absender.",
          "Emails go out via the platform's sender address, with the agency name as sender.", "Mails gehen über die Absenderadresse der Plattform, mit deinem Namen als Absender.", "Emails go out via the platform's sender address, with your name as sender."),
      };
  }
}

/** Wofür ein Eintrag da ist (Resend: record SPF mit MX bzw. TXT, DKIM, DMARC). */
export function recordPurpose(r: SenderDomainRecord): string {
  const kind = (r.record ?? "").toUpperCase();
  const type = (r.type ?? "").toUpperCase();
  if (kind === "DKIM") return t("Signatur (DKIM)", "Signature (DKIM)");
  if (kind === "SPF" && type === "MX") return t("Rückläufer (MX)", "Bounces (MX)");
  if (kind === "SPF") return t("Versand erlaubt (SPF)", "Sending allowed (SPF)");
  if (kind === "DMARC") return "DMARC";
  if (kind === "RECEIVING") return t("Empfang (MX)", "Receiving (MX)");
  return r.record ?? type;
}

/** Stand eines einzelnen Eintrags bei der Prüfung des Versanddienstes. */
export function recordStatusPill(status: string | undefined): Pill {
  switch (status) {
    case "verified": return pill(t("Gefunden", "Found"), "success");
    case "failed": return pill(t("Nicht gefunden", "Not found"), "error");
    case "temporary_failure": return pill(t("Vorübergehend nicht prüfbar", "Temporarily not checkable"), "warning");
    case "pending": return pill(t("Wird geprüft", "Being checked"), "warning");
    default: return pill(t("Noch nicht geprüft", "Not checked yet"), "neutral");
  }
}

/** Empfohlener DMARC-Eintrag (prüft der Versanddienst nicht; nur anlegen, wenn es für die Domain noch keinen gibt). */
export const DMARC_RECOMMENDATION: Required<Pick<SenderDomainRecord, "name" | "type" | "value">> = { name: "_dmarc", type: "TXT", value: "v=DMARC1; p=none;" };

/** „Jetzt prüfen“ ergibt Sinn, solange eine Domain gespeichert und nicht schon bestätigt ist. */
export function canCheckNow(status: SenderDomainStatus, hasDomain: boolean): boolean {
  return hasDomain && (status === "requested" || status === "pending" || status === "failed");
}
